/**
 * Build MongoDB match filters from FilterPanel array
 * Handles AND/OR logic, case-insensitive text searches, and field conflicts
 *
 * @param {Array} customFilters - Array of filter objects from FilterPanel
 * @returns {Object} MongoDB match query object
 */
export const buildCustomMatchFilters = (customFilters) => {
  if (!customFilters || customFilters.length === 0) return {};

  const orFilters = [];
  const andFilters = [];

  customFilters.forEach((filter, index) => {
    const { key, value, operator = "$eq", logic = "$and", type = "text" } = filter;

    if (!key || value === undefined || value === null || value === "") return;

    let filterValue;
    try {
      // Try to parse as JSON first (for complex queries like {"$gte": 5})
      filterValue = JSON.parse(value);
    } catch {
      // If not valid JSON, use as is
      filterValue = value;
    }

    // Build the filter condition
    let condition;

    // For text type with $eq operator, use case-insensitive regex
    if (type === "text" && operator === "$eq" && typeof filterValue === "string") {
      condition = { [key]: { $regex: `^${filterValue}$`, $options: "i" } };
    } else if (operator === "$regex" && typeof filterValue === "string") {
      // For $regex operator, always add case-insensitive flag
      condition = { [key]: { $regex: filterValue, $options: "i" } };
    } else if (operator === "$eq") {
      condition = { [key]: filterValue };
    } else {
      condition = { [key]: { [operator]: filterValue } };
    }

    // First filter is always AND, subsequent filters use their logic setting
    if (index === 0 || logic === "$and") {
      andFilters.push(condition);
    } else {
      // OR logic
      orFilters.push(condition);
    }
  });

  // Combine filters based on logic
  const result = {};

  // First, check if we have any OR filters with conflicting fields
  if (orFilters.length > 0 && andFilters.length > 0) {
    const andFields = andFilters.map(f => Object.keys(f)[0]);
    const orFields = orFilters.map(f => Object.keys(f)[0]);
    const conflictingFields = andFields.filter(field => orFields.includes(field));

    if (conflictingFields.length > 0) {
      // If there's a conflict, move all filters with conflicting fields into $or
      const conflictingAndFilters = andFilters.filter(f => conflictingFields.includes(Object.keys(f)[0]));
      const nonConflictingAndFilters = andFilters.filter(f => !conflictingFields.includes(Object.keys(f)[0]));

      // Add non-conflicting AND filters directly
      nonConflictingAndFilters.forEach(filter => {
        Object.assign(result, filter);
      });

      // Combine all conflicting filters into $or (don't add non-conflicting ANDs)
      result.$or = [...conflictingAndFilters, ...orFilters];

      return result;
    }
  }

  // No conflicts between AND and OR - handle them separately

  // Handle AND filters - check for duplicate fields
  if (andFilters.length > 0) {
    const andFieldCounts = {};
    andFilters.forEach(filter => {
      const field = Object.keys(filter)[0];
      andFieldCounts[field] = (andFieldCounts[field] || 0) + 1;
    });

    // Check if we have duplicate fields in AND filters
    const hasDuplicateAndFields = Object.values(andFieldCounts).some(count => count > 1);

    if (hasDuplicateAndFields) {
      // Use $and operator for multiple conditions on same field
      result.$and = andFilters;
    } else {
      // No duplicates, add directly
      andFilters.forEach(filter => {
        Object.assign(result, filter);
      });
    }
  }

  // Handle OR filters (only if no conflicts were already handled above)
  if (orFilters.length > 0) {
    result.$or = orFilters;
  }

  return result;
};
